/**
 * collection-sort.js
 * Sets CSS order on product cards — in-stock: order 0, sold-out: order 1.
 * Uses a MutationObserver on the parent to avoid interfering with the grid.
 */

async function fetchAvailability(handle, total) {
  const pages = Math.ceil(total / 250);
  const results = await Promise.all(
    Array.from({ length: pages }, (_, i) =>
      fetch(`/collections/${handle}/products.json?limit=250&page=${i + 1}`)
        .then((r) => r.json())
        .then((d) =>
          d.products.map((p) => ({
            id: String(p.id),
            available: p.variants.some((v) => v.available),
          })),
        ),
    ),
  );
  return results.flat();
}

function applyOrder(soldOutIds) {
  const items = [
    ...document.querySelectorAll("[js-product-grid] > [data-product-id]"),
  ];

  const bannerIndex = items.findIndex((el) =>
    el.dataset.productId.startsWith("custom-banner"),
  );

  if (bannerIndex === -1) {
    // No banner — simple in-stock/sold-out sort
    items.forEach((card) => {
      card.style.order = soldOutIds.has(card.dataset.productId) ? "1" : "0";
    });
    return;
  }

  // Number of product slots that should appear before the banner
  const slotsNeeded = bannerIndex;

  // In-stock items that already sit before the banner in the DOM
  const inStockBefore = items
    .slice(0, bannerIndex)
    .filter((el) => !soldOutIds.has(el.dataset.productId));

  // How many more in-stock products to pull from after the banner
  const toPromote = Math.max(0, slotsNeeded - inStockBefore.length);

  // First in-stock items after the banner (candidates for promotion)
  const inStockAfter = items
    .slice(bannerIndex + 1)
    .filter((el) => !soldOutIds.has(el.dataset.productId));

  // Build order map: assign negative order values so promoted products
  // appear before the banner (order:0) without reordering each other
  const orderMap = new Map();
  let counter = -slotsNeeded;
  inStockBefore.forEach((el) => orderMap.set(el.dataset.productId, counter++));
  inStockAfter.slice(0, toPromote).forEach((el) => orderMap.set(el.dataset.productId, counter++));

  items.forEach((card) => {
    const id = card.dataset.productId;
    if (id.startsWith("custom-banner")) return;
    if (orderMap.has(id)) {
      card.style.order = String(orderMap.get(id));
    } else {
      card.style.order = soldOutIds.has(id) ? "1" : "0";
    }
  });
}

const CACHE_VERSION = "v1";

function getCacheKey(handle, total) {
  return `kiyoko-sort:${CACHE_VERSION}:${handle}:${total}`;
}

function readCache(handle, total) {
  try {
    const raw = sessionStorage.getItem(getCacheKey(handle, total));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeCache(handle, total, products) {
  try {
    sessionStorage.setItem(
      getCacheKey(handle, total),
      JSON.stringify(products),
    );
  } catch {} // ignore if storage is full
}

async function run() {
  const section = document.querySelector("[data-kiyoko-sort]");
  if (!section) return;

  const handle = section.dataset.collectionHandle;
  const total = parseInt(section.dataset.collectionProductCount, 10);
  if (!handle || !total) return;

  const grid = document.querySelector("[js-product-grid]");
  if (!grid) return;

  // Cards are hidden via #kiyoko-sort-hide in theme.liquid head
  // Remove that style tag to reveal all cards at once after sorting
  const reveal = () => {
    const hideStyle = document.getElementById("kiyoko-sort-hide");
    if (hideStyle) hideStyle.remove();
  };
  const fallback = setTimeout(reveal, 2000);

  // Use cached availability if available — instant on repeat visits
  let products = readCache(handle, total);
  if (!products) {
    products = await fetchAvailability(handle, total);
    writeCache(handle, total, products);
  }

  clearTimeout(fallback);
  const soldOut = new Set(
    products.filter((p) => !p.available).map((p) => p.id),
  );

  applyOrder(soldOut);
  reveal();

  // Watch the PARENT of the grid for new cards being added
  // This avoids triggering on our own style changes
  const parent = grid.parentElement;
  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        // Only react to new product card <li> elements
        if (
          node.nodeType === 1 &&
          node.matches("[data-product-id]") &&
          !node.style.order
        ) {
          applyOrder(soldOut);
          return;
        }
      }
    }
  }).observe(parent, { childList: true, subtree: true });
}

document.addEventListener("DOMContentLoaded", run);
